<script setup lang="ts">
definePageMeta({
  pageTransition: false
})
import { computed, onMounted, onUnmounted, ref, watch, type Ref, shallowRef, type Component } from 'vue'
import ContextMenuView from '@/components/base/menu/ContextMenuView.vue'
import { useI18n } from 'vue-i18n'
import type ContextMenuItem from '../../../type/ContextMenuItem'
import {
  listPhotosByPage,
  type LifeFileWithRelationDTO,
  type ListMemorialFileResponse,
  mediaPreviewIndex,
  mediaPreviewList,
  isMediaPreviewOpen,
  mediaCanEdit,
  TYPE_AUDIO,
  sourceShouldRefresh,
  mediaPreviewType
} from '@/composables/memorize/memorize'
import { useRoute } from 'vue-router'
import { handleErr } from '@/composables/exception/handler'
import type { CommonResponse } from '@/apis/api'
import { CODE_OK } from '@/apis/code'
import { renderMessage, WAIT_FOR_LOADING_TIME } from '@/utils/util'
import { ElMessage } from 'element-plus'
import BaseLayout from '@/components/business/layout/BaseLayout.vue'
import { useServicesStore } from '@/composables/memorize/store'
import { useRouter } from 'vue-router'
import { TYPE_IMG, TYPE_VIDEO } from '@/composables/memorize/memorize'
import { titleBar } from '@/composables/menu/title'
import { isCreatorMember } from '@/composables/memorize/memorize'
import { debounce } from 'perfect-debounce'
import AlbumTypeSelector from '@/components/business/memorize/album/AlbumTypeSelector.vue'
import MediaTypeSelector from '@/components/business/memorize/album/MediaTypeSelector.vue'
import AlbumSkeleton from '@/components/business/skeleton/AlbumSkeleton.vue'
import {
  FOLDER_TYPE_YEAR,
  FOLDER_TYPE_COMMON,
  FOLDER_TYPE_NONE,
  preAddAlbumFile,
  useAlbumFolderStore,
  type PreAddAlbumFileResponse
} from '@/composables/album/album'
import { isSetRealName } from '@/composables/user/users'
import { isNotEmpty } from '@/utils/util'
import { useScheduleStore } from '@/composables/schedule/schedule'
import SetRealNameDialog from '@/components/business/me/SetRealNameDialog.vue'
import { auditModeShow } from '@/utils/util'
import { sanitizeForSerialization } from '@/utils/serialization'

// Lazy load heavy components
const MediaWaterfall = shallowRef<Component | null>(null)
const AlbumFolderList = shallowRef<Component | null>(null)
const AlbumSetting = shallowRef<Component | null>(null)
const MFooter = shallowRef<Component | null>(null)
const EmptyView = shallowRef<Component | null>(null)
const StorageOverCap = shallowRef<Component | null>(null)
const RActionButton = shallowRef<Component | null>(null)

const realNameModalRef = ref<InstanceType<typeof SetRealNameDialog> | null>(null)

const scheduleStore = useScheduleStore()
const albumFolderStore = useAlbumFolderStore()

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const serviceId = ref()
const lastId: Ref<string | null> = ref(null)
const lastRank: Ref<number> = ref(0)
const pageSize = ref(import.meta.server ? 30 : 100)
const totalSize = ref(0)

const collapse = ref(false)
const expandButtonVisible = ref(true)
const serviceDetailsStore = useServicesStore()
const serviceDetails = computed(() => serviceDetailsStore.searchForItem(serviceId.value))

const canEdit = computed(() => {
  const permission = serviceDetails.value?.privilege?.permissions.find((item) => item.name === 'm_album')
  return permission?.value && permission.value !== 'READ'
})

const overflowNotMember = ref(false)
const albumType = ref('waterfall')
const mediaType = ref('all')

const mediaList: Ref<Array<LifeFileWithRelationDTO>> = ref([])
const actionMenuGroup: Ref<Array<Array<ContextMenuItem>>> = ref([
  auditModeShow.value
    ? [{ name: t('action.add_picture'), key: 'add_picture', icon: 'i-iconoir:add-media-image' } as ContextMenuItem]
    : [
        { name: t('action.add_picture'), key: 'add_picture', icon: 'i-iconoir:add-media-image' } as ContextMenuItem,
        { name: t('action.add_video'), key: 'add_video', icon: 'i-iconoir:media-video-plus' } as ContextMenuItem,
        { name: t('action.add_audio'), key: 'add_audio', icon: 'i-solar:soundwave-outline' } as ContextMenuItem
      ]
])

const removeQueryParams = (name?: string) => {
  if (name) {
    const { [name]: _, ...rest } = route.query
    router.replace({ query: rest })
  } else {
    router.replace({ query: {} })
  }
}

const scheduleParams = scheduleStore.getActionDialogParams()
serviceId.value = route.params.id as string
const mActionOnce = { addMedia: isNotEmpty(route.query.addMedia) }

const MfooterVisible = ref(false)
const showFooter = ref(true)
const footerStatus = ref('none')
const hasMoreItems = ref(true)
const showCollapseBtn = ref(false)
const innerWidth = ref(0)
const componentsLoaded = ref(false)

const { data: ssrData } = await useAsyncData(`album-media-${serviceId.value}`, async () => {
  const { data } = await listPhotosByPage(serviceId.value, mediaType.value, null, 0, pageSize.value)
  return data.value ? sanitizeForSerialization(data.value) : null
})

async function loadComponents() {
  const [waterfallModule, folderModule, settingModule, footerModule, emptyModule, storageModule, buttonModule] =
    await Promise.all([
      import('@/components/business/memorize/MediaWaterfall.vue'),
      import('@/components/business/memorize/album/AlbumFolderList.vue'),
      import('@/components/business/memorize/album/AlbumSetting.vue'),
      import('@/components/business/memorize/MFooter.vue'),
      import('@/components/business/placeholder/EmptyView.vue'),
      import('@/components/business/member/StorageOverCap.vue'),
      import('@/components/base/RActionButton/RActionButton.vue')
    ])

  MediaWaterfall.value = waterfallModule.default
  AlbumFolderList.value = folderModule.default
  AlbumSetting.value = settingModule.default
  MFooter.value = footerModule.default
  EmptyView.value = emptyModule.default
  StorageOverCap.value = storageModule.default
  RActionButton.value = buttonModule.default
  componentsLoaded.value = true
}

onMounted(() => {
  void loadComponents()

  innerWidth.value = window.innerWidth
  showCollapseBtn.value = window.innerWidth >= 768

  const storedType = albumFolderStore.albumFolderType
  albumFolderStore.clearAlbumFolderType()
  if (storedType === FOLDER_TYPE_YEAR) {
    albumType.value = 'timeline'
  } else if (storedType === FOLDER_TYPE_COMMON || storedType === FOLDER_TYPE_NONE) {
    albumType.value = 'album'
  } else {
    albumType.value = 'waterfall'
  }

  window.addEventListener('scroll', scrollListener)
  window.addEventListener('resize', debounced)

  void preloadData()

  if (!ssrData.value || ssrData.value.code !== CODE_OK) {
    getData()
  }

  if (mActionOnce.addMedia && scheduleParams) {
    setTimeout(() => {
      router.push(
        `/addPhoto?type=${scheduleParams.type}&serviceId=` +
          serviceId.value +
          '&name=' +
          scheduleParams.name +
          '&tag=' +
          scheduleParams.tag +
          '&key=' +
          scheduleParams.key
      )
    }, 400)
  }

  if (import.meta.client && route.query.addMedia) {
    removeQueryParams('addMedia')
  }
})

const dataState = ref(ssrData.value && ssrData.value.code === CODE_OK ? 'done' : 'loading')

function handleSsrData() {
  if (!ssrData.value) return
  const resData = ssrData.value
  if (resData.code === CODE_OK) {
    const response: ListMemorialFileResponse = resData.data
    mediaList.value = response.items || []

    if (mediaList.value.length > 0) {
      dataState.value = 'done'
    } else {
      dataState.value = 'empty'
    }

    overflowNotMember.value = response.nonMemberStorageOverflow && response.items.length > 0
    totalSize.value = response.total
    footerStatus.value = 'none'
    hasMoreItems.value = true

    if (response.items.length === 0) {
      hasMoreItems.value = false
    } else {
      const lastItem = mediaList.value[mediaList.value.length - 1]
      if (lastItem) {
        lastId.value = lastItem.id
        lastRank.value = lastItem.rank
      }
    }

    // Delay footer visibility slightly as in original logic
    setTimeout(() => {
      MfooterVisible.value = true
    }, 500)
  }
}

if (ssrData.value && ssrData.value.code === CODE_OK) {
  handleSsrData()
}

const debounced = debounce(() => {
  innerWidth.value = window.innerWidth
  showCollapseBtn.value = window.innerWidth >= 768
}, 100)

function getData() {
  if (albumType.value === 'waterfall') {
    lastRank.value = 0
    loadMedia(lastRank.value, true)
    setTimeout(() => {
      if (dataState.value === 'none') {
        dataState.value = 'loading'
      }
    }, WAIT_FOR_LOADING_TIME)
  } else {
    dataState.value = 'done'
  }
}

onUnmounted(() => {
  window.removeEventListener('scroll', scrollListener)
  window.removeEventListener('resize', debounced)
})

async function onActionClicked(item: ContextMenuItem) {
  if (!isSetRealName.value) {
    const isSuccess = await realNameModalRef.value?.open()
    if (!isSuccess) {
      console.log('用户取消了实名认证，停止后续操作')
      return
    }
  }
  let type = TYPE_IMG
  if (item.key == 'add_video') {
    type = TYPE_VIDEO
  } else if (item.key == 'add_audio') {
    type = TYPE_AUDIO
  }
  router.push(
    `/uploadAlbumFile?type=${type}&serviceId=` + serviceId.value + '&name=' + titleBar.value?.life?.name.fullName
  )
}

async function preloadData() {
  const { data, error, statusCode } = await preAddAlbumFile(serviceId.value)
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as Ref<CommonResponse<PreAddAlbumFileResponse>>
    if (resData.value.code === CODE_OK) {
      albumFolderStore.setCommonFolder(serviceId.value, resData.value.data.commonFolders)
      albumFolderStore.setYearFolder(serviceId.value, resData.value.data.yearFolders)
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
    }
  }
}

function reloadData(newType: string) {
  if (newType === 'waterfall') {
    lastRank.value = 0
    loadMedia(lastRank.value, true)
  }
}

function tryAgain() {
  if (footerStatus.value === 'failed') {
    if (mediaList.value.length == 0) {
      lastRank.value = 0
      loadMedia(lastRank.value, true)
    } else {
      loadMedia(lastRank.value, false, lastId.value)
    }
  }
}

async function loadMedia(rank: number = 0, isInitial: boolean = false, last: string | null = null) {
  if (isInitial) {
    lastId.value = null
    lastRank.value = 0
  }

  footerStatus.value = 'loading'
  MfooterVisible.value = false
  const { data, error, statusCode } = await listPhotosByPage(
    serviceId.value,
    mediaType.value,
    last,
    rank,
    pageSize.value
  )
  setTimeout(() => {
    MfooterVisible.value = true
  }, 500)
  if (error.value) {
    handleErr(data, error, statusCode)
    if (last === null) {
      dataState.value = 'error'
    } else {
      footerStatus.value = 'failed'
    }
  } else {
    const resData = data as unknown as Ref<CommonResponse<ListMemorialFileResponse>>
    if (resData.value.code === CODE_OK) {
      const response: ListMemorialFileResponse = resData.value.data

      if (last === null) {
        mediaList.value = response.items

        if (mediaList.value.length > 0) {
          dataState.value = 'done'
        } else {
          dataState.value = 'empty'
        }
        overflowNotMember.value = response.nonMemberStorageOverflow && response.items.length > 0
      } else {
        mediaList.value.push(...response.items)
      }

      totalSize.value = response.total
      footerStatus.value = 'none'
      hasMoreItems.value = true

      if (response.items.length === 0) {
        hasMoreItems.value = false
      } else {
        const lastItem = response.items[response.items.length - 1]
        if (last !== null && lastItem.id === last) {
          hasMoreItems.value = false
        } else {
          lastId.value = lastItem.id
          lastRank.value = lastItem.rank
        }
      }
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
      if (last === null) {
        dataState.value = 'error'
      } else {
        footerStatus.value = 'failed'
      }
    }
  }
}

const scrollListener = function () {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const scrollHeight = document.documentElement.scrollHeight
  const clientHeight = document.documentElement.clientHeight
  if (scrollTop + clientHeight + 10 >= scrollHeight) {
    if (hasMoreItems.value && footerStatus.value !== 'loading') {
      if (albumType.value === 'waterfall') {
        loadMedia(lastRank.value, false, lastId.value)
      }
    }
  }
}

function onPreview(item: LifeFileWithRelationDTO) {
  mediaPreviewIndex.value = mediaList.value.findIndex((i) => i.id === item.file.id)
  mediaPreviewType.value = ''
  mediaPreviewList.value = mediaList.value
  isCreatorMember.value = serviceDetails.value?.isCreatorMember?.member || false
  isMediaPreviewOpen.value = true
  mediaCanEdit.value = canEdit.value
}

function onSorted(sortedMediaList: Array<LifeFileWithRelationDTO>) {
  mediaList.value = sortedMediaList
}

watch(
  () => albumType.value,
  (newVal, oldVal) => {
    if (newVal == 'waterfall') {
      expandButtonVisible.value = true
      reloadData('waterfall')
    } else {
      collapse.value = false
      expandButtonVisible.value = false
      reloadData('post')
    }
  }
)

watch(
  () => mediaType.value,
  (newVal, oldVal) => {
    if (newVal !== oldVal && albumType.value === 'waterfall') {
      reloadData('waterfall')
    }
  }
)

const showSetting = ref(false)
const settingInitialed = ref(false)
function tryShowSetting() {
  if (!settingInitialed.value) {
    settingInitialed.value = true
    setTimeout(() => {
      showSetting.value = true
    }, 50)
  } else {
    showSetting.value = true
  }
}

watch(
  () => sourceShouldRefresh.value,
  (newVal) => {
    if (newVal) {
      if (albumType.value === 'waterfall') {
        reloadData('waterfall')
        sourceShouldRefresh.value = false
      }
    }
  }
)

const canManage = computed(() => {
  const role = serviceDetails.value?.privilege?.role
  return role && (role === 'creator' || role === 'admin')
})

const pageTitle = computed(() => {
  const fullName = serviceDetails.value?.life?.name?.fullName
  if (auditModeShow.value) {
    return t('m.tag_title_audit', { fullName: fullName })
  } else if (inMiniprograme.value) {
    return t('m.tag_title_mini', { fullName: fullName })
  }
  return fullName ? t('m.memorial_hall_title', { name: fullName }) : t('m.memorial_hall_default')
})

const requestURL = useRequestURL()
const canonicalUrl = computed(() => `${requestURL.origin}${route.path}`)

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  description: () => t('m.album_desc', { name: serviceDetails.value?.life?.name?.fullName }),
  ogDescription: () => t('m.album_desc', { name: serviceDetails.value?.life?.name?.fullName }),
  ogImage: () => serviceDetails.value?.life?.avatar?.url || serviceDetails.value?.life?.avatar?.middleQuality,
  ogUrl: canonicalUrl,
  twitterCard: 'summary_large_image'
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl
    }
  ]
})
</script>
<template>
  <BaseLayout :collapse="collapse" :state="dataState" @retry="getData">
    <template #header>
      <div flex="~ col" w-full>
        <div w-full flex="~ row" my-3 items-center justify-between>
          <div flex="~ row" items-center w-auto grow>
            <AlbumTypeSelector v-if="componentsLoaded" v-model="albumType" mr-2 ml-1 />
            <button
              v-if="albumType === 'waterfall' && canManage && componentsLoaded"
              bg-transparent
              rounded-full
              p-2
              hover:bg-theme-light>
              <div i-carbon:settings text-2xl text-theme-dark @click="tryShowSetting"></div>
            </button>
            <component :is="StorageOverCap" v-if="overflowNotMember && componentsLoaded" />
          </div>
          <div flex="~ row" items-center>
            <ContextMenuView
              v-if="mediaList.length > 0 && canEdit && componentsLoaded"
              :menuGroups="actionMenuGroup"
              @onItemClicked="onActionClicked"
              text-theme-dark
              :theme="true">
              <template #trigger>
                <component
                  :is="RActionButton"
                  v-if="componentsLoaded"
                  md:py-1.6
                  md:rounded-full
                  mr-1
                  :title="$t('action.add')"
                  titleClass="md:text-theme-main"
                  :serviceId="serviceId"
                  name="">
                  <div i-mdi:image-add-outline text-2xl mr-2 class="md:text-theme-main"></div>
                </component>
              </template>
            </ContextMenuView>
            <div
              v-if="expandButtonVisible && showCollapseBtn && componentsLoaded"
              btn-hint
              border-1
              py-0.8
              px-3
              rounded-full
              flex-r-center
              w-14
              ml-2
              h-9.5
              @click="collapse = !collapse">
              <i
                rotate-90
                :class="collapse ? 'i-quill:collapse' : 'i-quill:expand'"
                inline-block
                text-xl
                text-second
                transition-all />
            </div>
          </div>
        </div>
        <div flex="~ row" justify-start items-center pl-2 v-if="!auditModeShow">
          <MediaTypeSelector v-if="albumType === 'waterfall' && componentsLoaded" v-model="mediaType" mb-2 />
        </div>
      </div>
    </template>

    <component
      :is="MediaWaterfall"
      v-if="albumType === 'waterfall' && mediaList.length > 0 && componentsLoaded"
      :mediaList="mediaList"
      :canEdit="canEdit"
      :creatorMember="!!serviceDetails?.isCreatorMember?.member"
      @onPreview="onPreview" />

    <component
      :is="AlbumFolderList"
      v-if="albumType == 'album' && componentsLoaded"
      :serviceId="serviceId"
      :canEdit="canEdit" />
    <component
      :is="AlbumFolderList"
      v-if="albumType == 'timeline' && componentsLoaded"
      timeline
      :serviceId="serviceId"
      :canEdit="canEdit" />

    <component
      :is="MFooter"
      v-if="albumType == 'waterfall' && MfooterVisible && componentsLoaded"
      v-model="showFooter"
      :loading="footerStatus"
      :hasMore="hasMoreItems"
      @click="tryAgain" />

    <template #skeleton>
      <AlbumSkeleton />
    </template>

    <template #fullscreen>
      <component
        :is="AlbumSetting"
        v-if="settingInitialed && componentsLoaded"
        v-model="showSetting"
        :serviceId="serviceId"
        :mediaList="mediaList"
        @sorted="onSorted" />
    </template>

    <template #empty>
      <component
        :is="EmptyView"
        v-if="componentsLoaded"
        :msg="canEdit ? $t('empty.no_album', { name: serviceDetails?.life.name.fullName }) : $t('empty.no_album2')"
        :theme="true">
        <ContextMenuView :menuGroups="actionMenuGroup" @onItemClicked="onActionClicked" :theme="true">
          <template #trigger>
            <component
              :is="RActionButton"
              md:py-2
              px-6
              md:rounded-full
              v-if="canEdit"
              mt-20
              mr-1
              :title="$t('action.add')"
              :serviceId="serviceId"
              name=""
              titleClass="md:text-theme-main">
              <div i-mdi:image-add-outline text-2xl mr-2></div>
            </component>
          </template>
        </ContextMenuView>
      </component>
    </template>
    <SetRealNameDialog ref="realNameModalRef" />
  </BaseLayout>
</template>
<style lang="scss" scoped></style>
